import { Graphics, useTick } from '@pixi/react';
import { useRef } from 'react';
import { tileWidth, tileHeight } from '../../data/tileset.config';
import {
  Graphics as PixiGraphics,
  Container,
  Sprite,
  Texture,
  SCALE_MODES,
} from 'pixi.js';
import { IAffectedTilePreview } from '../../types/types';
import { useGameStore } from '../../store/gameSlice';
import { dungeonService } from '../../services/DungeonService';
import { getZIndex } from '../../utils/zindex.utils';

// Reticle frame overlaid on each preview cell. Its transparent interior lets
// the per-spell color fill show through (cf. redraw). Player-owned previews use
// a distinct frame so the player can tell their own targeting apart.
function reticleTexture(path: string): Texture {
  const tex = Texture.from(path);
  tex.baseTexture.scaleMode = SCALE_MODES.NEAREST;
  return tex;
}
const PREVIEW_TEXTURE = reticleTexture('/sprites/effects/preview.png');
const PLAYER_PREVIEW_TEXTURE = reticleTexture(
  '/sprites/effects/player-preview.png',
);

// Corner radius applied to the grey background + the color fill only (matches
// the reticle frame's ~4px/32px corner inset). The progress circle is a plain
// inscribed circle, not subject to this rounding.
const RETICLE_CORNER_RATIO = 10 / 32;
const PREVIEW_BG_COLOR = 0x52525a;
const PREVIEW_BG_ALPHA = 1;

interface TileRect {
  posX: number;
  posY: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

function tileRect(tile: IAffectedTilePreview, zoomFactor: number): TileRect {
  const posX = tile.x * tileWidth * zoomFactor;
  const posY = -tile.y * tileHeight * zoomFactor;
  const tileW = tileWidth * zoomFactor;
  const tileH = tileHeight * zoomFactor;
  const rectW = tileW * 0.75;
  const rectH = tileH * 0.75;
  return {
    posX,
    posY,
    x: posX - tileW / 2 + (tileW - rectW) / 2,
    y: posY - tileH / 2 + (tileH - rectH) / 2,
    width: rectW,
    height: rectH,
  };
}

function redraw(
  g: PixiGraphics,
  tiles: readonly IAffectedTilePreview[],
  zoomFactor: number,
): void {
  g.clear();

  for (const tile of tiles) {
    const r = tileRect(tile, zoomFactor);
    const radius = Math.min(r.width, r.height) * RETICLE_CORNER_RATIO;
    const color = tile.color ?? 0xff0000;
    const alpha = tile.alpha ?? 0.3;

    g.beginFill(PREVIEW_BG_COLOR, PREVIEW_BG_ALPHA);
    g.drawRoundedRect(r.x, r.y, r.width, r.height, radius);
    g.endFill();

    g.beginFill(color, alpha);
    g.drawRoundedRect(r.x, r.y, r.width, r.height, radius);
    g.endFill();

    // Inscribed circle so it never spills past the rounded corners of the
    // cell — the corner radius is reserved for the rects above. Skipped for
    // tiles that opt out (e.g. residual damage zones: static tint, no timer).
    if (!tile.noProgressFill) {
      const maxRadius = Math.min(r.width, r.height) / 2;
      const progression =
        tile.duration && tile.duration > 0
          ? Math.min((tile.elapsedTime ?? 0) / tile.duration, 1)
          : 1;
      const circleAlpha = Math.min(alpha + 0.2, 1);

      g.beginFill(color, circleAlpha);
      g.drawCircle(r.posX, r.posY, maxRadius * progression);
      g.endFill();
    }
  }
}

/**
 * Reconciles a pool of reticle sprites with the current tiles: reuses sprites
 * across frames (keyed by tile id), repositions them, and destroys the ones
 * whose tile is gone. Avoids per-frame allocation / React re-renders.
 */
function syncReticles(
  layer: Container,
  pool: Map<string, Sprite>,
  tiles: readonly IAffectedTilePreview[],
  zoomFactor: number,
): void {
  const seen = new Set<string>();
  for (const tile of tiles) {
    const r = tileRect(tile, zoomFactor);
    let sprite = pool.get(tile.id);
    if (!sprite) {
      sprite = new Sprite(
        tile.isPlayer ? PLAYER_PREVIEW_TEXTURE : PREVIEW_TEXTURE,
      );
      sprite.anchor.set(0, 0);
      layer.addChild(sprite);
      pool.set(tile.id, sprite);
    }
    sprite.x = r.x;
    sprite.y = r.y;
    sprite.width = r.width;
    sprite.height = r.height;
    seen.add(tile.id);
  }
  for (const [id, sprite] of pool) {
    if (!seen.has(id)) {
      sprite.destroy();
      pool.delete(id);
    }
  }
}

/**
 * Drawn directly in `useTick` via a Pixi ref, without React setState. The
 * reticle `Container` is created once and reused every frame; otherwise a new
 * child would be `addChild`ed each frame then orphaned, leaking memory and
 * growing the draw-call count over time.
 */
export const AffectedTilesOverlayAlone = () => {
  const graphicsRef = useRef<PixiGraphics | null>(null);
  const reticleLayerRef = useRef<Container | null>(null);
  const reticlePoolRef = useRef<Map<string, Sprite>>(new Map());
  const hadTilesRef = useRef(false);

  useTick((deltaTime) => {
    dungeonService.previewService.updateAffectedTiles(deltaTime);

    const g = graphicsRef.current;
    if (!g) {
      return;
    }

    if (!reticleLayerRef.current) {
      const layer = new Container();
      reticleLayerRef.current = layer;
      g.addChild(layer);
    }

    const tiles = dungeonService.previewService.getAffectedTiles();
    // Skip the frame if there is nothing to draw and nothing was drawn last
    // frame — avoids a needless `clear()` on the Graphics.
    if (tiles.length === 0 && !hadTilesRef.current) {
      return;
    }
    hadTilesRef.current = tiles.length > 0;

    const zoomFactor = useGameStore.getState().zoomFactor;
    redraw(g, tiles, zoomFactor);
    syncReticles(
      reticleLayerRef.current,
      reticlePoolRef.current,
      tiles,
      zoomFactor,
    );
  });

  return (
    <Graphics ref={graphicsRef} zIndex={getZIndex({ type: 'attackPreview' })} />
  );
};
